// MUI theme for the v2 layout. The internal token table (formerly the
// public V2_TOKENS object) is now embedded directly into the MUI theme via
// module augmentation, so consumers read `theme.palette.surface.raised`,
// `theme.palette.border.soft`, `theme.typography.fontFamilyMono`, etc.
// instead of importing a parallel constants object.

import { createTheme, type ThemeOptions } from '@mui/material/styles';

// Internal token table — values that fed the previous V2_TOKENS export.
// Kept in this module (not exported) as the single source of truth wired
// into the MUI theme below. Update colors/sizes here.
export const themeTokens = {
    // Surfaces (low → high)
    bgCanvas: '#0a0a0b',
    bgCanvasTransparency: '#0a0a0bDD',
    bgPanel:  '#111114',
    bgRaised: '#17171b',
    bgHover:  '#1d1d22',

    // Borders
    border:     '#26262d',
    borderSoft: '#1f1f25',

    divider: '#161616',

    // Text
    text:      '#ececef',
    textDim:   '#8e8e96',
    textFaint: '#5a5a62',

    // Brand / status
    accent:     '#e0a458',                   // amber — primary
    accentDim:  '#7a5a30',
    accentSoft: 'rgba(224,164,88,0.14)',
    good:       '#5eb88a',
    bad:        '#d97a7a',                   // coral — secondary

    // Typography
    font: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',

    // Layout primitives
    radius:    10,
    radiusSm:  6,
    radiusLg:  12,
} as const;

// ---- MUI module augmentation ---------------------------------------------
// Extends the MUI Theme so v2-specific surfaces, borders, brand soft tints,
// the mono font family, and small/large radii are first-class theme reads.
//
// surface.chrome / surface.chromePanel are OPTIONAL inverted-region tokens
// (a dark app-shell/sidebar over a light canvas). This dark theme leaves them
// unset; light consumer themes (e.g. examples/demo's limeTheme) populate them.
// Optional keeps every existing theme — including this one — valid.

declare module '@mui/material/styles' {
    interface Palette {
        surface: { panel: string; raised: string; hover: string; chrome?: string; chromePanel?: string };
        border:  { default: string; soft: string };
    }
    interface PaletteOptions {
        surface?: { panel: string; raised: string; hover: string; chrome?: string; chromePanel?: string };
        border?:  { default: string; soft: string };
    }
    interface PaletteColor {
        softBg?: string;
    }
    interface SimplePaletteColorOptions {
        softBg?: string;
    }
    interface TypographyVariants {
        fontFamilyMono: string;
    }
    interface TypographyVariantsOptions {
        fontFamilyMono?: string;
    }
}

// `Shape` lives in @mui/system; augmenting it there flows through to
// `theme.shape.radiusSm` / `theme.shape.radiusLg` in @mui/material.
declare module '@mui/system' {
    interface Shape {
        radiusSm: number;
        radiusLg: number;
    }
}

// --------------------------------------------------------------------------

const themeOptions: ThemeOptions = {
    palette: {
        mode: 'dark',
        primary: {
            main: themeTokens.accent,
            dark: themeTokens.accentDim,
            softBg: themeTokens.accentSoft,
            contrastText: '#1a1208',
        },
        secondary: {
            // Coral — used wherever the design previously reached for the
            // `bad` token (negative trend chips, etc.). This is distinct
            // from `error.main`, which keeps MUI's default red for genuine
            // error states (matching the v1 palette).
            main: themeTokens.bad,
        },
        success: {
            main: themeTokens.good,
        },
        // `error.main` intentionally inherits MUI's default (#f44336) so
        // error semantics stay distinguishable from the secondary brand.
        background: {
            default: themeTokens.bgCanvas,
            paper:   themeTokens.bgPanel,
        },
        text: {
            primary:   themeTokens.text,
            secondary: themeTokens.textDim,
            disabled:  themeTokens.textFaint,
        },
        divider: themeTokens.divider,
        surface: {
            panel:  themeTokens.bgPanel,
            raised: themeTokens.bgRaised,
            hover:  themeTokens.bgHover,
        },
        border: {
            default: themeTokens.border,
            soft:    themeTokens.borderSoft,
        },
    },
    shape: {
        borderRadius: themeTokens.radius,
        radiusSm: themeTokens.radiusSm,
        radiusLg: themeTokens.radiusLg,
    },
    typography: {
        // Heading variants (h1..h6) are intentionally NOT overridden so
        // MUI's default rem-based scale applies — that keeps content
        // surfaces like MetricCard (which renders KPI values via
        // <Typography variant="h4">) at a readable size while the v2
        // chrome uses explicit sx fontSize for fine control.
        fontFamily: themeTokens.font,
        fontFamilyMono: themeTokens.mono,
        fontSize: 13,
        body1: { fontSize: 13, lineHeight: 1.55 },
        body2: { fontSize: 12, lineHeight: 1.5 },
        caption: { fontSize: 11, color: themeTokens.textDim },
        button: { textTransform: 'none', fontWeight: 500 },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                'html, body': {
                    backgroundColor: themeTokens.bgCanvas,
                    color: themeTokens.text,
                    fontFamily: themeTokens.font,
                },
                '*': {
                    boxSizing: 'border-box',
                },
                'code, pre, .mono': {
                    fontFamily: themeTokens.mono,
                },
            },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: {
                    borderRadius: themeTokens.radiusSm,
                    textTransform: 'none',
                    fontWeight: 500,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    backgroundColor: themeTokens.bgRaised,
                    border: `1px solid ${themeTokens.border}`,
                    color: themeTokens.textDim,
                    fontSize: 11,
                    height: 24,
                    borderRadius: 999,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: themeTokens.textDim,
                    borderRadius: themeTokens.radiusSm,
                    '&:hover': { backgroundColor: themeTokens.bgRaised },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    backgroundColor: themeTokens.bgPanel,
                    border: `1px solid ${themeTokens.border}`,
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: { borderColor: themeTokens.border },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: themeTokens.bgRaised,
                    border: `1px solid ${themeTokens.border}`,
                    color: themeTokens.text,
                    fontSize: 11,
                },
            },
        },
        MuiTextField: {
            defaultProps: { size: 'small', variant: 'outlined' },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: themeTokens.bgRaised,
                    fontSize: 12,
                    '& fieldset': { borderColor: themeTokens.border },
                    '&:hover fieldset': { borderColor: themeTokens.borderSoft },
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: { padding: 0 },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: themeTokens.radiusSm,
                    '&.Mui-selected': {
                        backgroundColor: themeTokens.bgHover,
                        color: themeTokens.accent,
                        '& .MuiListItemIcon-root': { color: themeTokens.accent },
                        '&:hover': { backgroundColor: themeTokens.bgHover },
                    },
                    '&:hover': { backgroundColor: themeTokens.bgRaised },
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: themeTokens.bgPanel,
                    borderBottom: `1px solid ${themeTokens.border}`,
                    boxShadow: 'none',
                },
            },
        },
    },
};

export const v2Theme = createTheme(themeOptions);
export type V2Theme = typeof v2Theme;
